import * as React from "react";
import { DraggableLogic, ReduxFilters } from "../reducers/BusinessLogic";
import { getOrderIndexFromGantt } from "./DraggableElements";
import { IS_INSIDE_ORDER, TRANSLATE_CURSOR, DRAGGING_ORDER } from "./reducers";

import type {
    DraggingOrderAction,
    ResetDraggedOrderAction,
    IsInsideOrderAction,
    TranslateCursorAction,
} from "./reducers";

const INSIDE_ORDER_EVENT = "inside_order_event";

type ActionTypes = DraggingOrderAction | ResetDraggedOrderAction | IsInsideOrderAction | TranslateCursorAction;

type InsideOrderEventDetail = {
    line_uuid: string;
    is_unscheduled: boolean;
    x: number;
    y: number;
    width: number;
    height: number;
    is_extra_line: boolean;
    line_index?: number;
    earliest_end: number;
    source_type: string;
    production_version: string;
    inside_order_uuid: string;
};

const disableDrop = (is_inside_order: boolean, dispatch: React.Dispatch<ActionTypes>) => {
    if (is_inside_order) {
        dispatch({ type: IS_INSIDE_ORDER, data: false });
    }
    DraggableLogic.changeMouseCursor("not-allowed");
};

const enableDrop = (is_inside_order: boolean, dispatch: React.Dispatch<ActionTypes>) => {
    if (is_inside_order) {
        dispatch({ type: IS_INSIDE_ORDER, data: true });
    }
    DraggableLogic.changeMouseCursor("move");
};

export class DraggableContextHelper {
    inside_order_listener: ((this: Window, e: CustomEvent) => void) | null = null;

    constructor(
        private dragging_order_line_uuid: string,
        private dragging_order_uuid: string,
        private dispatch: React.Dispatch<ActionTypes> | undefined,
        private is_multiselect: boolean
    ) {}

    addListeners = () => {
        this.inside_order_listener = (e: CustomEvent) => this.onInsideDraggableOrder(e.detail);
        // @ts-ignore
        window.addEventListener(INSIDE_ORDER_EVENT, this.inside_order_listener);
    };

    removeListeners = () => {
        if (this.inside_order_listener) {
            // @ts-ignore
            window.removeEventListener(INSIDE_ORDER_EVENT, this.inside_order_listener);
        }
    };

    onInsideDraggableOrder = (event_detail: InsideOrderEventDetail) => {
        const {
            line_uuid,
            is_unscheduled,
            x,
            y,
            width,
            is_extra_line,
            line_index,
            earliest_end,
            source_type,
            production_version,
            inside_order_uuid,
        } = event_detail;

        if (!this.dragging_order_uuid || !this.dispatch) {
            return;
        }

        const is_inside_order = true;
        const is_droppable =
            ReduxFilters.isDroppableLineUuid(line_uuid, this.dragging_order_uuid, is_unscheduled) &&
            !(this.is_multiselect && this.dragging_order_line_uuid !== line_uuid);

        if (!is_droppable) {
            disableDrop(is_inside_order, this.dispatch);
            return;
        }
        enableDrop(is_inside_order, this.dispatch);

        if (is_extra_line) {
            disableDrop(is_inside_order, this.dispatch);
            return;
        }

        const translate_x = x + width;

        enableDrop(is_inside_order, this.dispatch);

        this.dispatch({
            type: TRANSLATE_CURSOR,
            data: {
                translate_x,
                translate_y: y,
            },
        });

        const new_order_index = getOrderIndexFromGantt(line_index, this.dragging_order_uuid, earliest_end, source_type);

        this.dispatch({
            type: DRAGGING_ORDER,
            data: {
                translate_x,
                translate_y: y,
                new_line_uuid: line_uuid,
                new_order_index,
                line_index: line_index,
                earliest_start: earliest_end,
                is_unscheduled_order: is_unscheduled,
                production_version: production_version,
                override_earliest_start: earliest_end,
                inside_order_uuid,
            },
        });
    };
}

export class DraggableEventHandler {
    static dispatchInsideOrderEvent = (event_detail: InsideOrderEventDetail) => {
        const event = new CustomEvent(INSIDE_ORDER_EVENT, { detail: event_detail });
        window.dispatchEvent(event);
    };
}
